// Where a piece ran, and the clipping from each of those places.
//
// A press release goes to ten papers, so this is a multi-select over the managed
// outlet list — never free text, which is what produced "המבשר" and "מבשר" as
// separate entries and made "how many times did we appear there" unanswerable.
//
// The ten-clippings problem: the cost is not ten controls on screen, it is ten trips
// through the file-picker dialog. So there are two ways in — pick many files at once
// and assign them (with the outlet guessed from each filename), or attach one
// directly to a card. The cards themselves are a checklist: the header counts how
// many of the outlets still have no cutting.
import { useState } from 'react';
import { useLookups } from '../context/LookupsContext';
import { settingsApi } from '../api';
import he from '../i18n/he';
import { messageForError } from '../i18n/errors';
import Button from './Button';
import OutletCard from './OutletCard';

let stagedId = 0;

export default function OutletSection({
  outlets, onChange, files, canAttach, onAttach, onRemoveFile,
}) {
  const lookups = useLookups();
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [staged, setStaged] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const selected = outlets || [];
  const isSelected = (code) => selected.some((o) => o.outlet_code === code);
  const clippingFor = (code) => (files || []).find((f) => f.outlet_code === code);
  const withClipping = selected.filter((o) => clippingFor(o.outlet_code)).length;

  const toggle = (code) => {
    if (isSelected(code)) removeOutlet(code);
    else onChange([...selected, { outlet_code: code, ran_on: null, url: null }]);
  };

  // Refuse locally, naming the paper. The server refuses too (409
  // outlet_has_clipping) — this is the version that can say which one before a
  // round trip, so an image never disappears as a side effect of an edit.
  const removeOutlet = (code) => {
    if (clippingFor(code)) {
      // A ready-made sentence, not an error code — so it is rendered as-is below.
      setError(`${he.publications.outletHasClipping}: ${lookups.outletLabel(code)}`);
      return;
    }
    setError(null);
    onChange(selected.filter((o) => o.outlet_code !== code));
  };

  const updateOutlet = (next) =>
    onChange(selected.map((o) => (o.outlet_code === next.outlet_code ? next : o)));

  // Creating an outlet goes through the same settings endpoint the Settings screen
  // uses, so the code is generated and the list stays canonical — no leaving the
  // form mid-flow. The new paper appears immediately as an empty card, which is
  // itself the cue to attach its clipping.
  const addOutlet = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setError(null);
    try {
      const created = await settingsApi.add('outlets', { label_he: label });
      await lookups.reload();
      onChange([...selected, { outlet_code: created.code, ran_on: null, url: null }]);
      setNewLabel('');
      setAdding(false);
      setSearch('');
    } catch (e) {
      setError(e);
    }
  };

  // Bulk pick: one dialog for all ten cuttings. Each file's outlet is guessed from
  // its filename, but ONLY on an unambiguous single match — a wrong guess nobody
  // notices is worse than no guess at all, so it is a pre-filled dropdown, never a
  // silent decision.
  const stage = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    const guess = (name) => {
      const hits = selected.filter((o) => name.includes(lookups.outletLabel(o.outlet_code)));
      return hits.length === 1 ? hits[0].outlet_code : '';
    };
    setStaged((cur) => [
      ...cur,
      ...picked.map((file) => {
        stagedId += 1;
        return { key: stagedId, file, outlet_code: guess(file.name), status: 'pending' };
      }),
    ]);
  };

  const setStagedOutlet = (key) => (e) =>
    setStaged((cur) => cur.map((s) => (s.key === key ? { ...s, outlet_code: e.target.value } : s)));

  // Sequential on purpose: ten phone photos uploading in parallel on a home
  // connection reads as stalled rather than fast, and one failure stays legible.
  const uploadStaged = async () => {
    setError(null);
    setBusy(true);
    for (const item of staged.filter((s) => s.outlet_code && s.status !== 'done')) {
      setStaged((cur) => cur.map((s) => (s.key === item.key ? { ...s, status: 'uploading' } : s)));
      try {
        await onAttach(item.file, item.outlet_code);
        setStaged((cur) => cur.filter((s) => s.key !== item.key));
      } catch (e) {
        setStaged((cur) => cur.map((s) => (s.key === item.key
          ? { ...s, status: 'error', error: messageForError(e) } : s)));
      }
    }
    setBusy(false);
  };

  const term = search.trim();
  const options = lookups.outletOptions.filter((o) => !term || o.label.includes(term));

  return (
    <div className="field">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <label>{he.publications.outlets}</label>
        {selected.length > 0 && (
          <span className="muted">
            {withClipping} {he.common.of} {selected.length} {he.publications.clippings}
          </span>
        )}
      </div>

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={he.publications.outletSearch}
      />
      <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        {options.map((o) => (
          <Button
            key={o.value}
            small
            variant={isSelected(o.value) ? '' : 'subtle'}
            onClick={() => toggle(o.value)}
          >
            {o.label}
          </Button>
        ))}
      </div>

      {adding ? (
        <div className="row" style={{ gap: 8, marginTop: 8 }}>
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder={he.publications.outletAddPrompt}
          />
          <Button small onClick={addOutlet}>{he.common.save}</Button>
          <Button small variant="subtle" onClick={() => { setAdding(false); setNewLabel(''); }}>
            {he.common.cancel}
          </Button>
        </div>
      ) : (
        <Button small variant="subtle" onClick={() => setAdding(true)} style={{ marginTop: 8 }}>
          {he.publications.outletAdd}
        </Button>
      )}

      {selected.length === 0 ? (
        <p className="muted">{he.publications.noOutlets}</p>
      ) : (
        <>
          {canAttach && (
            <div className="bulk-strip">
              <label className="bulk-pick">
                <span>{he.publications.clippingsBulk}</span>
                <input type="file" multiple onChange={stage} disabled={busy} hidden />
              </label>
              {staged.map((s) => (
                <div key={s.key} className="row staged-file" style={{ gap: 6 }}>
                  <span className="file-name" title={s.file.name}>{s.file.name}</span>
                  <select value={s.outlet_code} onChange={setStagedOutlet(s.key)}>
                    <option value="">{he.publications.clippingAssign}</option>
                    {selected.map((o) => (
                      <option key={o.outlet_code} value={o.outlet_code}>
                        {lookups.outletLabel(o.outlet_code)}
                      </option>
                    ))}
                  </select>
                  {s.status === 'uploading' && <span className="muted">{he.publications.uploading}</span>}
                  {s.status === 'error' && <span className="error-text">{s.error}</span>}
                  <Button
                    small
                    variant="subtle"
                    onClick={() => setStaged((cur) => cur.filter((x) => x.key !== s.key))}
                  >
                    ×
                  </Button>
                </div>
              ))}
              {staged.length > 0 && (
                <Button small onClick={uploadStaged} disabled={busy || !staged.some((s) => s.outlet_code)}>
                  {he.publications.clippingsUpload}
                </Button>
              )}
            </div>
          )}

          <div className="outlet-grid">
            {selected.map((o) => (
              <OutletCard
                key={o.outlet_code}
                outlet={o}
                label={lookups.outletLabel(o.outlet_code)}
                clipping={clippingFor(o.outlet_code)}
                canAttach={canAttach}
                busy={busy}
                onChange={updateOutlet}
                onRemove={() => removeOutlet(o.outlet_code)}
                onAttach={(file) => onAttach(file, o.outlet_code).catch((e) => setError(e))}
                onRemoveClipping={onRemoveFile}
              />
            ))}
          </div>
        </>
      )}

      {/* `error` is either a ready Hebrew sentence (the local guards, which can name
          the outlet) or a thrown error carrying a server code to be mapped. */}
      {error && (
        <p className="error-text">{typeof error === 'string' ? error : messageForError(error)}</p>
      )}
    </div>
  );
}
